import { FunctionComponent, useState } from "react";
import { t } from "i18next";

import { TooltipWrapper } from "../../../components/Shared/Components/TooltipWrapper/TooltipWrapper";
import { translationKeys } from "../../../models/app-translation-keys";

import "./ImageResolutionControl.scss";

import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { classNames } from "primereact/utils";

import { ImageSize } from "../../../../@api";

import { useStore } from "../../../stores/StoreContext";

export interface IChangeImageResolutionPayload {
  imageResolution: ImageSize;
}

interface IImageResolutionControlProps {
  imageResolution: ImageSize;
  disabled: boolean;
  onChange: (imageResolution: ImageSize) => void;
}

const featureTranslationKeys = translationKeys.feature.imageGeneration;
const imageResolutionControlTranslationKeys = featureTranslationKeys.imageResolutionControl;

export const ImageResolutionControl: FunctionComponent<IImageResolutionControlProps> = ({
  imageResolution,
  disabled,
  onChange,
}) => {
  const [isImageResolutionValid, setIsImageResolutionValid] = useState(true);

  const { globalErrorsStore } = useStore();

  const imageResolutionOptions = [
    {
      value: ImageSize.Auto,
      label: t(imageResolutionControlTranslationKeys.imageResolutionLabel),
    },
    { value: ImageSize.Size1024X1024, label: "1024 x 1024" },
    { value: ImageSize.Size1024X1536, label: "1024 x 1536" },
    { value: ImageSize.Size1536X1024, label: "1536 x 1024" },
  ];

  const handleImageResolutionChange = (dropdownChangeEvent: DropdownChangeEvent) => {
    const valid = Object.values(ImageSize).includes(dropdownChangeEvent.value);

    setIsImageResolutionValid(valid);

    if (!valid) {
      globalErrorsStore.addErrorIfNotExists(featureTranslationKeys.errors.invalidResolution);
      console.error("[ImageResolutionControl] Invalid image resolution: ", dropdownChangeEvent.value);
    } else {
      onChange(dropdownChangeEvent.value);
      globalErrorsStore.removeError(featureTranslationKeys.errors.invalidResolution);
    }
  };

  return (
    <div className="image-resolution-dropdown-with-icon">
      <TooltipWrapper
        targetRef=".image-resolution"
        position="top"
        content={t(
          isImageResolutionValid
            ? imageResolutionControlTranslationKeys.imageResolutionTooltip
            : featureTranslationKeys.errors.invalidResolution,
        )}
        disabled={disabled}
      />

      <span className="left-icon" aria-hidden="true">
        <i className="pi pi-sliders-h" />
      </span>

      <Dropdown
        invalid={!isImageResolutionValid}
        className={classNames("image-resolution", { error: !isImageResolutionValid })}
        data-testid="image-resolution-dropdown"
        value={imageResolution}
        options={imageResolutionOptions}
        optionLabel="label"
        optionValue="value"
        onChange={handleImageResolutionChange}
        placeholder={t(imageResolutionControlTranslationKeys.imageResolutionLabel)}
        disabled={disabled}
        aria-label={t(imageResolutionControlTranslationKeys.imageResolutionTooltip)}
      />
    </div>
  );
};
